import { useCallback, useEffect, useState } from 'react';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';
import type { PlayOption } from '@shared/flow/play-catalog';
import type { GongFlowSummary } from '@shared/flow/gong-flow-catalog';
import {
  CustomInviteLookupResponseSchema,
  CustomInviteStatusResponseSchema,
  EnrollResponseSchema,
  type CustomInviteLookupResponse,
  type EnrollResultRow,
} from '@shared/flow/custom-invite';
import { useApi } from '../../../lib/api';
import { ApiProblemError, fetchJson, fetchRepSafeJson } from '../../../lib/apiFetchJson';
import { validateResponse } from '../../../lib/validateResponse';
import { useEnrollQueue } from './EnrollQueueContext';
import { FLOW_CONTACT_STATE_KEY } from '../contact-state-keys';
import { invalidateFlowList } from '../flow-list-invalidation';

/** Module scope, stable identity — see `PlayPicker`'s `gongFlowsSuggested`
 *  prop doc comment; this panel never surfaces Gong at all (K6), but a
 *  fresh `[]` literal every render would still defeat memoization inside
 *  `PlayPicker`. */
export const EMPTY_GONG_FLOWS: GongFlowSummary[] = [];

export const READ_ONLY_MESSAGE = 'Custom Invite is read-only while impersonating';

const EMAIL_SCHEMA = z.string().email();

/** Everything a send needs, snapshotted at the click (F2). Nothing in the
 *  mutation reads render state after `mutate(vars)` — TanStack v5 runs
 *  `onSuccess` against the LATEST render's closure at settle time, so a rep
 *  who clicked "Change" or edited the name mid-flight would otherwise crash
 *  the callback (`selectedPlay!.name` on null) or mislabel the toast. */
interface SendVars {
  campaignId: string;
  campaignName: string;
  senderSelection: string;
  email: string;
  name: string;
  /** `lookup.data?.contact != null` at click time — drives the toast description. */
  contactFound: boolean;
  /** Item L — trimmed rep-editable overrides of the campaign's own copy. */
  subject: string;
  emailCopy: string;
}

/**
 * Cleanup item e (rail-glaze-parity QA round, found adjacent to
 * `ActionsDetail.tsx` rather than inside it) — `sendMutation`'s `mutationFn`
 * already attaches a per-contact `code` (e.g. `'sender_unresolved'`) via
 * `Object.assign(new Error(message), { code })` below, but the mutation was
 * typed against a bare `Error`, so every reader of `sendMutation.error` had
 * to re-cast past `undefined` to see it. Typing `useMutation`'s `TError`
 * generic as `SendError` makes `.error` already carry `code` — no cast at
 * any call site, including `InvitePane.tsx`'s "Send as yourself instead"
 * recovery affordance.
 */
type SendError = Error & { code?: string };

/**
 * `CustomInvitePanel`'s entire form/data layer, MOVED VERBATIM out of
 * `CustomInvitePanel.tsx` (U6, rail-glaze-parity dispatch) so a later unit
 * can restyle that panel's JSX without touching any of this logic. Nothing
 * about the behaviour changed in the move; every comment below is the
 * original's.
 */
export function useCustomInviteForm(identityKey: string | null) {
  const { close, plays, activePlayTypes, sendersFor, defaultSenderFor, readOnly } = useEnrollQueue();
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  const eventInviteType = activePlayTypes.find((t) => t.play_type_slug === 'event_invite');
  const eventInvitePlays = useMemo(() => plays.filter((p) => p.play_type_slug === 'event_invite'), [plays]);
  const eventInviteTypes = useMemo(
    () => activePlayTypes.filter((t) => t.play_type_slug === 'event_invite'),
    [activePlayTypes],
  );
  const senders = sendersFor(eventInviteType?.play_type_id ?? null);
  const defaultSender = defaultSenderFor(eventInviteType?.play_type_id ?? null);
  // Item J — mirrors `listSenderOptions`' own guard (sender-resolution.ts):
  // `'self'` is omitted from `senders` entirely when the acting user has no
  // `ownerEmail`, so this is the same signal the server already used to
  // decide the option isn't offered. Reusing it here means the "Send as
  // yourself instead" retry button never appears somewhere it would just
  // throw a second, different error.
  const selfSenderAvailable = senders.some((s) => s.value === 'self');

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [nameSource, setNameSource] = useState<'typed' | 'lookup' | null>(null);
  const [lookedUpEmail, setLookedUpEmail] = useState<string | null>(null);
  const [selectedPlay, setSelectedPlay] = useState<PlayOption | null>(null);
  const [sender, setSender] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [emailCopy, setEmailCopy] = useState('');
  // Item L — which campaign's copy is currently seeded into subject/emailCopy.
  // A render-phase state adjustment (same pattern as the name-autofill block
  // below), NOT a useEffect keyed on `selectedPlay` directly: seeds ONCE per
  // NEW campaign selection (a changed `play_instance_ref`), so a rep's edit
  // survives any unrelated re-render of the same selected play, but picking a
  // different campaign (including via "Change") always reseeds to that
  // campaign's own copy — never a stale mix of one campaign's subject with
  // another's body.
  const [seededForPlayRef, setSeededForPlayRef] = useState<string | null>(null);
  if (selectedPlay && seededForPlayRef !== selectedPlay.play_instance_ref) {
    setSeededForPlayRef(selectedPlay.play_instance_ref);
    setSubject(selectedPlay.invite_subject ?? '');
    setEmailCopy(selectedPlay.invite_body ?? '');
  }

  const normalizedEmail = email.trim().toLowerCase();
  const emailValid = EMAIL_SCHEMA.safeParse(normalizedEmail).success;
  const lookupCurrent = lookedUpEmail === normalizedEmail;

  // Seed the sender once the role-derived default resolves — idempotent
  // (StrictMode-safe): re-running this effect after the default resolves a
  // SECOND time (it doesn't, in practice) would still no-op once `sender`
  // is non-null. Deliberately excludes `sender` from the deps so clearing
  // it later never re-triggers a reseed.
  useEffect(() => {
    if (defaultSender.resolved && sender === null) {
      setSender(defaultSender.sender);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `sender` is read only to guard re-seeding; including it would re-run this effect every time the rep changes the sender, which is the exact reseed this guards against.
  }, [defaultSender.resolved, defaultSender.sender]);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      if (nameSource === 'lookup') {
        setName('');
        setNameSource(null);
      }
    },
    [nameSource],
  );

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameSource('typed');
  }, []);

  const lookup = useQuery<CustomInviteLookupResponse>({
    queryKey: ['custom-invite-lookup', identityKey, lookedUpEmail],
    enabled: lookedUpEmail !== null,
    staleTime: Infinity,
    retry: false,
    // This panel renders its own found/not-found/error feedback inline
    // (below) — opting out of the global QueryCache toast avoids double-
    // surfacing the same failure (mutation-cache-handler.ts's documented
    // `suppressErrorToast` convention).
    meta: { suppressErrorToast: true },
    queryFn: async () => {
      // U12 (finding 4-5) — routes through the shared rep-safe helper so a
      // rejected `fetch` or an unparsable body renders this fallback sentence
      // instead of the raw `Error` (e.g. "Failed to fetch") reaching the
      // `Alert` below via `lookup.error.message`.
      const data = await fetchRepSafeJson(
        apiFetch,
        `/api/flow/enroll/lookup?email=${encodeURIComponent(lookedUpEmail as string)}`,
        { fallback: "Couldn't look up that email right now." },
      );
      return validateResponse(CustomInviteLookupResponseSchema, data, 'GET /api/flow/enroll/lookup');
    },
  });

  const handleLookup = useCallback(() => {
    if (!emailValid) return;
    if (lookedUpEmail === normalizedEmail) {
      // Same email as the current lookup: `setLookedUpEmail` would be a
      // same-value no-op and `retry: false` means the query never retries
      // itself — so a failed (or stale) lookup could never be re-run from
      // the button. `refetch()` forces the fetch regardless of staleTime.
      void lookup.refetch();
      return;
    }
    setLookedUpEmail(normalizedEmail);
    // Depend on the whole `lookup` object, not `lookup.refetch` — oxlint's
    // react-hooks(exhaustive-deps) treats a bare member expression as an
    // incomplete dependency here. `useQuery` (TanStack v5) returns a new
    // object every render regardless, so this has no effect on how often
    // the callback identity changes; `refetch` itself is still a stable
    // bound method underneath.
  }, [emailValid, normalizedEmail, lookedUpEmail, lookup]);

  const handleEmailKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleLookup();
    },
    [handleLookup],
  );

  // Name autofill — a render-phase state adjustment (React's "adjusting
  // state when data changes" pattern), NOT a useEffect: `terra/no-query-
  // seeded-effect` bans a useEffect that seeds state straight from a query
  // `.data` dependency (an SSE-driven background refetch would re-run it
  // and clobber an in-progress edit). This panel lives outside the v4/SSE
  // tree (plan Context — the dock mounts as an AuthenticatedLayout sibling,
  // not inside the artifact tree), but the guard below is still exactly
  // right for this panel's own concern: apply the autofill only ONCE per
  // resolved FETCH (`lastAutofilledAt` tracks the `dataUpdatedAt` of the
  // response we've already reacted to). Keyed per fetch, not per email
  // (round-2 G6): F4's `refetch()` can resolve the SAME `lookedUpEmail` more
  // than once — a not-found response followed, after the contact is
  // created, by a found response must still autofill — while a stale
  // re-render of an already-handled response never re-applies it, and a
  // typed name (`nameSource === 'typed'`) is never clobbered.
  // `lookupCurrent` gates it too: a response for `lookedUpEmail` that lands
  // after the rep has already typed a different email must not attach that
  // contact's name to the new email — the autofill is deferred until the
  // rep looks THAT email up (or types the original back, at which point the
  // cached data applies).
  const [lastAutofilledAt, setLastAutofilledAt] = useState<number | null>(null);
  if (lookup.data && lookedUpEmail !== null && lookupCurrent && lastAutofilledAt !== lookup.dataUpdatedAt) {
    setLastAutofilledAt(lookup.dataUpdatedAt);
    const contact = lookup.data.contact;
    if (contact?.full_name && nameSource !== 'typed') {
      setName(contact.full_name);
      setNameSource('lookup');
    }
  }

  const inviteStatus = useQuery({
    queryKey: ['custom-invite-status', identityKey, normalizedEmail, selectedPlay?.play_instance_ref],
    enabled: emailValid && lookupCurrent && selectedPlay !== null,
    staleTime: Infinity,
    retry: false,
    // Rendered inline as the "already invited" warning below — see the
    // `lookup` query's own `meta` doc comment.
    meta: { suppressErrorToast: true },
    queryFn: () =>
      fetchJson(
        apiFetch,
        CustomInviteStatusResponseSchema,
        `/api/flow/enroll/invite-status?email=${encodeURIComponent(normalizedEmail)}&campaignId=${encodeURIComponent(
          selectedPlay!.play_instance_ref,
        )}`,
      ),
  });

  const sendMutation = useMutation<EnrollResultRow, SendError, SendVars>({
    // Rendered inline as an error Alert next to Send below (K8) rather than
    // through `useMutation`'s own `onError` — `terra/no-toast-in-mutation-
    // onerror` bans calling `toast.error()` there (the global
    // `MutationCache.onError` → `notifyError` is the app's single error-
    // toast surface, and it can't show this per-contact server message
    // without misclassifying it as a generic failure). `suppressErrorToast`
    // opts this mutation out of that global toast, matching
    // `PlaysPage.tsx`'s `testMutation` convention for the same reason.
    meta: { suppressErrorToast: true },
    mutationFn: async (vars: SendVars) => {
      const body = {
        campaignId: vars.campaignId,
        senderSelection: vars.senderSelection,
        subject: vars.subject,
        emailCopy: vars.emailCopy,
        contacts: [{ email: vars.email, name: vars.name }],
      };
      let data;
      try {
        data = await fetchJson(apiFetch, EnrollResponseSchema, '/api/flow/enroll', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          timeoutMs: 60_000,
        });
      } catch (err) {
        if (!(err instanceof ApiProblemError) || err.kind !== 'http') throw err;
        // 422 with { results } (all failed) OR { error: { message } } — prefer the per-contact message.
        let message = err.userMessage ?? 'Invite failed';
        // Item J — threads the per-contact `code` (e.g. `'sender_unresolved'`)
        // onto the thrown Error too, so `sendMutation.error` carries it and
        // the error-render block below can offer the "Send as yourself
        // instead" recovery without matching on message prose.
        let code: string | undefined;
        const parsed = EnrollResponseSchema.safeParse(err.body);
        if (parsed.success && parsed.data.results[0]?.message) message = parsed.data.results[0].message;
        if (parsed.success) code = parsed.data.results[0]?.code;
        throw Object.assign(new Error(message), { code });
      }
      const row = data.results[0];
      if (!row || row.status !== 'ok') throw Object.assign(new Error(row?.message ?? 'Invite failed'), { code: row?.code });
      return row;
    },
    onSuccess: (row, vars) => {
      toast.success(`Invite sent — ${vars.name} → ${vars.campaignName}`, {
        description: vars.contactFound
          ? undefined
          : 'They have no Salesforce contact yet — they will appear on the Plays tab after Clay creates the contact and the mirror syncs.',
      });
      // A new play membership changes People rows and can move a People saved view's favorite count.
      void invalidateFlowList(queryClient, 'contact');
      void queryClient.invalidateQueries({ queryKey: FLOW_CONTACT_STATE_KEY });
      void queryClient.invalidateQueries({ queryKey: ['custom-invite-status', identityKey, vars.email] });
      // U12 (findings 5-3, 4-1) — the lookup's own campaign-history cache is
      // `staleTime: Infinity` and keyed on email only, so re-looking-up the
      // SAME email after a send would otherwise keep serving the pre-send
      // snapshot. A custom invite also writes a `launcher_invitee_actions`
      // row, which makes the invitee a "Not Responded" member of this play —
      // the Plays tab's member sections are `useFlowContacts` mounts
      // (plan 2026-09-25-002 U7), so the `['flow-contacts']` invalidation
      // above already covers them; no separate key is needed.
      void queryClient.invalidateQueries({ queryKey: ['custom-invite-lookup', identityKey, vars.email] });
      setEmail('');
      setName('');
      setNameSource(null);
      setLookedUpEmail(null);
      setLastAutofilledAt(null);
      setSelectedPlay(null);
      setSubject('');
      setEmailCopy('');
      setSeededForPlayRef(null);
      // Sender is kept — see plan U5 "Send" section.
    },
  });

  const handleSend = useCallback(() => {
    // `sendDisabled` already guarantees these at click time; the narrowing is for TypeScript, not a second gate.
    if (!selectedPlay || !sender) return;
    sendMutation.mutate({
      campaignId: selectedPlay.play_instance_ref,
      campaignName: selectedPlay.name,
      senderSelection: sender,
      email: normalizedEmail,
      name: name.trim(),
      contactFound: lookup.data?.contact != null,
      subject: subject.trim(),
      emailCopy: emailCopy.trim(),
    });
    // Depend on the whole `sendMutation` object, not `sendMutation.mutate` —
    // oxlint's react-hooks(exhaustive-deps) treats a bare member expression
    // as an incomplete dependency here. `useMutation` (TanStack v5) returns
    // a new object every render regardless, so this has no effect on how
    // often the callback identity changes; `mutate` itself is still stable.
  }, [selectedPlay, sender, normalizedEmail, name, lookup.data, sendMutation, subject, emailCopy]);

  // Item J — the explicit "Send as yourself instead" retry after a
  // sender_unresolved failure. Reuses the LAST-ATTEMPTED mutation variables
  // (`sendMutation.variables`, exposed by TanStack v5's `useMutation`) rather
  // than re-reading component state: the rep may have edited the email/name/
  // campaign since the failed attempt, and the retry should resend exactly
  // what failed, just with a different sender.
  const handleRetryAsSelf = useCallback(() => {
    if (!sendMutation.variables) return;
    sendMutation.mutate({ ...sendMutation.variables, senderSelection: 'self' });
    setSender('self');
    // Depend on the whole `sendMutation` object, not `sendMutation.mutate` —
    // same oxlint exhaustive-deps note as `handleSend` above.
  }, [sendMutation]);

  const sendDisabled =
    !emailValid ||
    !lookupCurrent ||
    lookup.isFetching ||
    name.trim() === '' ||
    !selectedPlay ||
    !sender ||
    subject.trim() === '' ||
    emailCopy.trim() === '' ||
    readOnly ||
    sendMutation.isPending;

  return {
    close,
    readOnly,
    eventInviteType,
    eventInvitePlays,
    eventInviteTypes,
    senders,
    selfSenderAvailable,
    email,
    name,
    nameSource,
    lookedUpEmail,
    lookupCurrent,
    normalizedEmail,
    emailValid,
    selectedPlay,
    setSelectedPlay,
    sender,
    setSender,
    subject,
    setSubject,
    emailCopy,
    setEmailCopy,
    lookup,
    inviteStatus,
    sendMutation,
    handleEmailChange,
    handleNameChange,
    handleLookup,
    handleEmailKeyDown,
    handleSend,
    handleRetryAsSelf,
    sendDisabled,
  };
}
